// The two addresses sign-in and sign-out navigate to, checked before the navigation (E12-S1-T2).
// Pure functions with no React, so the dev stub API (dev/stub-api.ts) applies the same rule to
// /auth/login's returnTo as the app does before it sends the browser there.
//
// The return path after sign-in is a path on this origin and nothing else. Anything that a browser
// could resolve to another host is discarded for the default page:
//
//   https://evil.test/x   absolute
//   //evil.test/x         protocol-relative
//   /\evil.test/x         a backslash, which browsers read as a slash
//   /<TAB>/evil.test      whitespace or a control character, which URL parsers strip
//   learn, javascript:x   not starting with a slash
//
// The signed-out page is never a return path: signing in to land on "you are signed out" is a loop.

/** Where sign-in returns to when there is no usable return path. */
export const DEFAULT_RETURN_PATH = "/learn";

/** The local page sign-out ends on when the API's end-session address is local (E15-S4). */
export const SIGNED_OUT_PATH = "/signed-out";

const UNSAFE = /[\\\s\p{Cc}]/u;

/** A path on this origin: one leading slash, not two, no backslash, no whitespace or control character. */
export function isLocalPath(raw: string): boolean {
  return raw.startsWith("/") && !raw.startsWith("//") && !UNSAFE.test(raw);
}

/** The return path if it is a path on this origin, otherwise the default page. Never throws. */
export function safeReturnPath(raw: unknown): string {
  if (typeof raw !== "string" || !isLocalPath(raw)) return DEFAULT_RETURN_PATH;
  const path = raw.split(/[?#]/, 1)[0] ?? "";
  if (path === SIGNED_OUT_PATH || path.startsWith(`${SIGNED_OUT_PATH}/`))
    return DEFAULT_RETURN_PATH;
  return raw;
}

/** The address the 'Sign in' control navigates to: the API's login route with a safe return path. */
export function signInHref(returnTo: unknown): string {
  return `/auth/login?returnTo=${encodeURIComponent(safeReturnPath(returnTo))}`;
}

/**
 * Where sign-out goes once the API has ended the session: the address POST /auth/logout answered —
 * the identity provider's end-session URL (absolute https) or a local path. Anything else (another
 * scheme, a protocol-relative address) is not followed; the local signed-out page is shown instead,
 * since the API has already ended the session.
 */
export function endSessionAddress(redirectTo: string): string {
  if (isLocalPath(redirectTo)) return redirectTo;
  if (UNSAFE.test(redirectTo)) return SIGNED_OUT_PATH;
  try {
    const url = new URL(redirectTo);
    return url.protocol === "https:" ? url.href : SIGNED_OUT_PATH;
  } catch {
    return SIGNED_OUT_PATH;
  }
}
